"use client";

import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import ListSubheader from "@mui/material/ListSubheader";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import { useEffect } from "react";
import labelService from "../services/labelService";

function flattenLabels(labels, parentPath = "") {
  const seen = new Map();

  function walk(items, path = "") {
    for (const label of items) {
      const currentPath = path ? `${path} / ${label.name}` : label.name;

      // If we haven't seen this ID yet, add it to our map.
      // This ensures unique IDs for MenuItem keys and prevents duplicates.
      if (!seen.has(label.id)) {
        seen.set(label.id, {
          id: label.id,
          name: currentPath,
        });
      }

      if (label.children && label.children.length > 0) {
        walk(label.children, currentPath);
      }
    }
  }

  walk(labels, parentPath);
  return Array.from(seen.values());
}

export function CreateLabelDialog({ open, setOpen, fetchAllData, labelId }) {
  const [name, setName] = React.useState("");
  const [nest, setNest] = React.useState(true);
  const [parent, setParent] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [labels, setLabels] = React.useState([]);
  const [searching, setSearching] = React.useState(false);
  const [isEditMode, setIsEditMode] = React.useState(false);
  const [selectedParent, setSelectedParent] = React.useState(null);
  const [searchInput, setSearchInput] = React.useState("");

  const typingTimeout = React.useRef(null);
  const abortControllerRef = React.useRef(null);

  useEffect(() => {
    if (open) {
      fetchLabels("");
      if (labelId) {
        setIsEditMode(true);
        fetchLabelDetails(labelId);
      } else {
        setIsEditMode(false);
        setName("");
        setParent(null);
        setNest(true);
        setSelectedParent(null);
      }
    } else {
      // Reset search state when closed
      setSearchInput("");
      // Optionally pre-fetch labels or just clear them to ensure a fresh start next time
      setLabels([]);
    }
  }, [open, labelId]);

  async function fetchLabels(search = "") {
    // Abort previous in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    // Create new controller for this request
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setSearching(true);
      const res = await labelService.GET_Label_ALL_Search(search, controller.signal);

      // If this request was aborted during execution, stop processing
      if (controller.signal.aborted) return;

      const fetchedLabels = res.data?.labels || [];
      const flatLabels = flattenLabels(fetchedLabels);
      setLabels(flatLabels);
    } catch (error) {
      // Ignore cancellation errors
      if (error.name === "CanceledError" || error.code === "ERR_CANCELED" || error.name === "AbortError") {
        return;
      }
      console.error("Error fetching labels:", error);
    } finally {
      // Only set searching to false if this is still the active request
      if (abortControllerRef.current === controller) {
        setSearching(false);
      }
    }
  }

  async function fetchLabelDetails(id) {
    try {
      const res = await labelService.GET_Label_By_Id(id);
      const labelData = res.data?.data;



      if (labelData) {
        setName(labelData.name || "");

        if (labelData.ParentLabel && labelData.ParentLabel.id) {
          setParent(labelData.ParentLabel.id);
          setSelectedParent({
            id: labelData.ParentLabel.id,
            name: labelData.ParentLabel.name,
          });
          setNest(true);
        } else {
          setParent(null);
          setSelectedParent(null);
          setNest(false);
        }
      }
    } catch (error) {
      console.error("Error fetching label details:", error);
    }
  }

  async function handleSave() {
    if (!name.trim()) return;
    setLoading(true);


    const payload = {
      name,
      color: "#FF0000",
    };


    if (selectedParent && selectedParent.id) {
      payload.parentId = selectedParent.id;
    }


    if (isEditMode && (!selectedParent || !selectedParent.id)) {
      payload.parentId = null;
    }

    try {
      if (isEditMode && labelId) {
        await labelService.Update_Label_By_Id(labelId, payload);
      } else {
        await labelService.CREATE_Label(payload);
      }

      await fetchAllData();
      setOpen(false);
    } catch (error) {
      console.error("Error saving label:", error);
    } finally {
      setLoading(false);
    }
  }


  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      maxWidth="sm"
      fullWidth

      BackdropProps={{
        style: {
          backgroundColor: "rgba(255,255,255,0.2)",

        },
      }}

      PaperProps={{
        style: {
          borderRadius: 16,
          padding: "10px 16px",
          background: "rgba(255,255,255,0.9)",
          backdropFilter: "blur(2px)",
        },
      }}
    >
      <DialogTitle>{isEditMode ? "Edit Label" : "New Label"}</DialogTitle>

      <DialogContent>
        <TextField
          label="Label name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          margin="normal"
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={nest}
              onChange={(e) => setNest(e.target.checked)}
            />
          }
          label="Nest label under:"
        />

        <FormControl fullWidth margin="normal" disabled={!nest}>
          <InputLabel id="parent-label-select-label">Parent label</InputLabel>
          <Select
            labelId="parent-label-select-label"
            label="Parent label"
            value={selectedParent?.id || ""}
            onChange={(e) => {
              const selectedId = e.target.value;
              if (selectedId === "") {
                setParent(null);
                setSelectedParent(null);
                return;
              }
              const lbl = labels.find((l) => l.id === selectedId);
              setParent(selectedId);
              setSelectedParent(lbl || { id: selectedId, name: "Selected Label" });
            }}
            renderValue={(selectedId) => {
              if (!selectedId) return <em>None</em>;
              const lbl = labels.find((l) => l.id === selectedId) || selectedParent;
              return lbl?.name || selectedId;
            }}
            MenuProps={{
              autoFocus: false, // Prevents Select from stealing focus from its own search input
              PaperProps: {
                style: {
                  maxHeight: 400,
                },
              },
            }}
          >
            <ListSubheader disableSticky>
              <TextField
                size="small"
                autoFocus
                placeholder="Type to search..."
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                value={searchInput}
                onChange={(e) => {
                  const val = e.target.value;
                  setSearchInput(val);
                  if (typingTimeout.current) clearTimeout(typingTimeout.current);
                  typingTimeout.current = setTimeout(() => fetchLabels(val), 400);
                }}
                onKeyDown={(e) => {
                  if (e.key !== "Escape") {
                    // Prevents Select from closing when typing spaces etc.
                    e.stopPropagation();
                  }
                }}
              />
            </ListSubheader>
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {searching && labels.length === 0 ? (
              <MenuItem disabled>Searching...</MenuItem>
            ) : (
              labels
                .filter((lbl) =>
                  lbl.name.toLowerCase().includes(searchInput.toLowerCase())
                )
                .map((lbl) => (
                  <MenuItem key={lbl.id} value={lbl.id}>
                    {lbl.name}
                  </MenuItem>
                ))
            )}
            {!searching &&
              labels.filter((lbl) =>
                lbl.name.toLowerCase().includes(searchInput.toLowerCase())
              ).length === 0 && <MenuItem disabled>No labels found</MenuItem>}
          </Select>
        </FormControl>
      </DialogContent>

      <DialogActions>
        <Button variant="text" onClick={() => setOpen(false)}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!name.trim() || loading}
        >
          {loading
            ? isEditMode
              ? "Updating..."
              : "Creating..."
            : isEditMode
              ? "Update"
              : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
